import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/hooks/useStore';
import { useNavigate, useParams } from 'react-router-dom';
import { FORM_TYPE, FormType, LICENSE_TYPE_OPTION, STATUS } from '@/types/enums';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '@/components/common/Form';
import { ButtonCus, ButtonCusWithIcon } from '@/components/common/Button';
import yup from '@/services/yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    DATE_YMD_2,
    DATE_YMD_HMS,
    DEFAULT_SELECT_PAGE_SIZES,
    EMAIL_VALIDATE_REGEX,
    LICENSE_TYPE_OPTIONS,
    MAX_LENGTH_NUMBER,
    NUMBER_FULL_HALF_VALIDATE_REGEX,
    ROLE_GAS_OPTION_SELECT,
    ROUTES
} from '@/configs/constants';
import { Input } from '@/components/common/Input';
import Calendar from '@/components/calendar/Calendar';
import Combobox from '@/components/common/Combobox';
import { RadioGroup, RadioGroupItem } from '@/components/common/RadioGroup';
import { Label } from '@/components/common/Label';
import { flowResult } from 'mobx';
import { formatDateTime } from '@/utils/datetime';
import { useEffect, useMemo, useState } from 'react';
import ImageUploadAccount from '@/components/general/ImageUploadAccount';
import { Textarea } from '@/components/common/Textarea';
import PasswordAction from './components/PasswordAction';
import { isAdmin, isLicenseType, isOwner, replaceSpaces } from '@/utils/utils';
import { convertFullToHalf } from '@/utils/formatNumber';
import HistoryItem from './components/HistoryItem';
import { TableState, Updater } from '@tanstack/react-table';
import TableActionButtons from '@/components/common/TableActionButtons';
import { HistoryChangeUser } from '@/types/account';
import { ColumnDef } from '@tanstack/react-table';
import Table from '@/components/table/Table';
import { IconAge, IconUserCode } from '@/components/icon';

interface AccountScreenProps {
    formType: FormType
}

export default observer(function AddAccountGasPage({
    formType
}: AccountScreenProps) {
    const isEdit = formType === FORM_TYPE.Edit;

    // hook
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();

    // store
    const {
        accountStore: {
            account,
            createAccount,
            getAccount,
            editAccount,
            deleteAccount,
            //
            historyChangeUser,
            totalRecordHistoryChangeUser,
            pagingHistoryChangeUser,
            defaultPagingHistory,
            getHisoryChangeUser,
            cleanHistory,
            //
            setObservable
        },
        generalStore: { licenseTypeGas, getLicenseGas },
        modalStore: { showAlertModal, showModal, hideModal }
    } = useStore();

    // form
    const validateSchema = yup.object().shape({
        licenseType: yup
            .string()
            .when('roleId', {
                is: roleId => roleId && !isAdmin(Number(roleId)),
                then: _ => yup.string().required('form_error_validate.required')
            }),
        licenseTypeOption: yup
            .number()
            .when('roleId', {
                is: roleId => roleId && !isAdmin(Number(roleId)),
                then: _ => yup.string().required('form_error_validate.required')
            }),
        licenseBeforeImageId: yup
            .number()
            .when('roleId', {
                is: roleId => roleId && !isAdmin(Number(roleId)),
                then: _ => yup.number().required('form_error_validate.required'),
                otherwise: _ => yup.number().optional()
            }),
        licenseAfterImageId: yup
            .number()
            .when(['roleId', 'licenseTypeOption'], {
                is: (roleId, licenseTypeOption) => roleId && !isAdmin(Number(roleId)) && Number(licenseTypeOption) !== LICENSE_TYPE_OPTION.MY_NUMBER,
                then: _ => yup.number().required('form_error_validate.required'),
                otherwise: _ => yup.number().optional()
            }),
        fullname: yup
            .string()
            .required('form_error_validate.required'),
        furiganaName: yup
            .string(),
        birthOfDate: yup
            .date()
            // .nullable()
            .when('roleId', {
                is: roleId => roleId && !isAdmin(Number(roleId)),
                then: _ => yup.date().required('form_error_validate.required'),
                otherwise: _ => yup.date().nullable()
            }),
        licenseExporeDate: yup
            .date()
            .nullable(),
        licenseNumber: yup
            .string()
            .matches(NUMBER_FULL_HALF_VALIDATE_REGEX, 'form_error_validate.only_number'),
        email: yup
            .string()
            .trim()
            .required('form_error_validate.required')
            .matches(EMAIL_VALIDATE_REGEX, 'form_error_validate.email'),
        phone: yup
            .string()
            .required('form_error_validate.required')
            .matches(NUMBER_FULL_HALF_VALIDATE_REGEX, 'form_error_validate.only_number'),
        zipCode: yup
            .string()
            .matches(NUMBER_FULL_HALF_VALIDATE_REGEX, 'form_error_validate.only_number'),
        roleId: yup
            .string()
            .required('form_error_validate.required'),
        address: yup
            .string(),
        status: yup
            .number(),
        note: yup
            .string()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            licenseType: '',
            licenseTypeOption: LICENSE_TYPE_OPTION.TRADITIONAL,
            licenseBeforeImageId: undefined,
            licenseAfterImageId: undefined,
            fullname: '',
            furiganaName: '',
            birthOfDate: undefined,
            licenseExporeDate: null,
            licenseNumber: '',
            email: '',
            phone: '',
            zipCode: '',
            roleId: '',
            address: '',
            status: STATUS.Active,
            note: ''
        }
    });

    // state
    const [beforeImgUrl, setBeforeImgUrl] = useState<string>('');
    const [afterImgUrl, setAfterImgUrl] = useState<string>('');

    // watch form
    const watchRoleId = form.watch('roleId');
    const watchLicenseTypeOption = form.watch('licenseTypeOption');

    const useMyNumberLicense = useMemo((): boolean => {
        return Number(watchLicenseTypeOption) === LICENSE_TYPE_OPTION.MY_NUMBER;
    }, [watchLicenseTypeOption]);

    const roles = useMemo(() => {
        if (formType === FORM_TYPE.Edit && watchRoleId && isOwner(Number(watchRoleId))) {
            return ROLE_GAS_OPTION_SELECT?.filter(item => isOwner(Number(item.id)));
        }
        return ROLE_GAS_OPTION_SELECT;
    }, [formType, watchRoleId]);

    const disabledRole = useMemo((): boolean => {
        if (formType === FORM_TYPE.Create) {
            return false;
        }

        if (!watchRoleId) {
            return false;
        }

        if (!isOwner(Number(watchRoleId))) {
            return true;
        }
        return false;
    }, [formType, watchRoleId]);

    // lifecycle
    useEffect(() => {
        fetchDataLicense();
    }, []);

    useEffect(() => {
        fetchData(Number(id));

        return () => {
            setObservable('account', undefined);
            cleanHistory();
        };
    }, [id]);

    // function
    const fetchDataLicense = async () => {
        await flowResult(getLicenseGas());
    };

    const fetchData = async (id: number) => {
        if (id) {
            const res = await flowResult(getAccount(id));
            if (res) {
                const {
                    licenseType,
                    licenseTypeOption,
                    licenseBeforeImage,
                    licenseAfterImage,
                    fullname,
                    furiganaName,
                    birthOfDate,
                    licenseExporeDate,
                    licenseNumber,
                    email,
                    phone,
                    zipCode,
                    role,
                    address,
                    status,
                    note
                } = res.data;

                if (licenseBeforeImage?.path) {
                    setBeforeImgUrl(licenseBeforeImage?.path);
                }
                if (licenseAfterImage?.path) {
                    setAfterImgUrl(licenseAfterImage?.path);
                }

                form.reset({
                    licenseType: (Array.isArray(licenseType) && licenseType?.length > 0) ?
                        licenseType[0]?.toString() :
                        '',
                    licenseTypeOption: isLicenseType(Number(licenseTypeOption)) ? Number(licenseTypeOption) : undefined,
                    licenseBeforeImageId: licenseBeforeImage?.id || undefined,
                    licenseAfterImageId: licenseAfterImage?.id || undefined,
                    fullname: fullname?.toString() || '',
                    furiganaName: furiganaName?.toString() || '',
                    birthOfDate: birthOfDate ? new Date(birthOfDate) : undefined,
                    licenseExporeDate: licenseExporeDate ?
                        new Date(licenseExporeDate) :
                        undefined,
                    licenseNumber: licenseNumber ? convertFullToHalf(licenseNumber) : '',
                    email: email || '',
                    phone: phone ? convertFullToHalf(phone) : '',
                    zipCode: zipCode ? convertFullToHalf(zipCode) : '',
                    roleId: role?.id?.toString() || undefined,
                    address: address || '',
                    status: status,
                    note: note || ''
                });

                // get history
                onFetchHistory(Number(id), defaultPagingHistory);
            }
        }
    };

    const onSubmit = async (data: FormData) => {
        const {
            licenseType,
            licenseTypeOption,
            licenseBeforeImageId,
            licenseAfterImageId,
            fullname,
            furiganaName,
            birthOfDate,
            licenseExporeDate,
            licenseNumber,
            email,
            phone,
            zipCode,
            roleId,
            address,
            status,
            note
        } = data;

        const submitData = {
            licenseType: (licenseType && !isNaN(Number(licenseType))) ? [Number(licenseType)] : [],
            licenseTypeOption: licenseTypeOption || null,
            licenseBeforeImageId: licenseBeforeImageId || null,
            licenseAfterImageId: (!useMyNumberLicense && licenseAfterImageId) ? licenseAfterImageId : null,
            minorDriverLicense: null,
            fullname,
            furiganaName,
            birthOfDate: formatDateTime(birthOfDate || '', DATE_YMD_2),
            licenseExporeDate: formatDateTime(licenseExporeDate || '', DATE_YMD_2),
            licenseNumber: licenseNumber ? convertFullToHalf(licenseNumber) : '',
            email,
            phone: phone ? convertFullToHalf(phone) : '',
            zipCode: zipCode ? convertFullToHalf(zipCode) : '',
            roleId: Number(roleId),
            address,
            status,
            note
        };

        switch (formType) {
            case FORM_TYPE.Create: {
                const res = await flowResult(createAccount(submitData));
                if (res) {
                    showAlertModal({
                        id: 'add-account-success',
                        type: 'success',
                        content: t('messages.add_success'),
                        onClickXIcon: () => navigate(ROUTES.userManagement.href)
                    });
                }
                break;
            }
            case FORM_TYPE.Edit: {
                const res = await flowResult(editAccount({ id, data: submitData }));
                if (res) {
                    showAlertModal({
                        id: 'update-account-success',
                        type: 'success',
                        content: t('messages.update_success'),
                        onClickXIcon: () => navigate(ROUTES.userManagement.href)
                    });
                }
                break;
            }
        }
    };

    const uploadSuccessBefore = (value) => {
        if (useMyNumberLicense) {
            return;
        }

        form.setValue('licenseBeforeImageId', value?.licenseImage?.id || null);

        form.setValue('fullname', value?.licenseInfo?.name ? replaceSpaces(value?.licenseInfo?.name) : '');
        form.setValue(
            'birthOfDate',
            value?.licenseInfo?.dateOfBirth ?
                new Date(value?.licenseInfo?.dateOfBirth) :
                undefined
        );
        form.setValue(
            'licenseExporeDate',
            value?.licenseInfo?.dateOfExpiration ?
                new Date(value?.licenseInfo?.dateOfExpiration) :
                null
        );
        form.setValue('licenseNumber', value?.licenseInfo?.licenseNumber ? replaceSpaces(value?.licenseInfo?.licenseNumber) : '');
        form.setValue('address', value?.licenseInfo?.address ? replaceSpaces(value?.licenseInfo?.address) : '');
    };

    // api delete account by id
    const onDeleteLocationById = async (accountId: number) => {
        const res = await flowResult(deleteAccount(accountId));
        if (res?.ok) {
            showAlertModal({
                id: 'delete-account-success',
                type: 'success',
                content: t('messages.delete_success'),
                onClickXIcon: () => navigate(ROUTES.userManagement.href)
            });
        }
    };

    // handle click btn delete account
    const onDeleteAccount = () => {
        showAlertModal({
            id: 'delete-account',
            isConfirmModal: true,
            content: t('messages.confirm_delete'),
            onSave: () => {
                onDeleteLocationById(Number(id));
                hideModal('delete-account');
            }
        });
    };

    const getAgeText = (age: any): string => {
        if (age === 0 || age === '0') {
            return '0歳';
        }

        if (!age) {
            return '--';
        }

        const ageNumber = parseInt(age);
        return isNaN(ageNumber) ? '--' : `${ageNumber}歳`;
    };

    // #region History
    const onFetchHistory = async (userId: number, paging: any) => {
        await flowResult(getHisoryChangeUser({ userId }, paging));
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging =
            stateUpdater instanceof Function ? stateUpdater(pagingHistoryChangeUser) : stateUpdater;
        onFetchHistory(Number(id), newPaging);
    };

    const historyColumns: ColumnDef<HistoryChangeUser>[] = [
        {
            id: 'id',
            accessorKey: 'id',
            header: '',
            enableSorting: false,
            size: 50,
            cell: ({ row: { original } }) => (
                <TableActionButtons onView={() => onDetailHistory(original)} />
            )
        },
        {
            accessorKey: 'updateAt',
            header: t('field.update_time'),
            size: 300,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div>
                    {formatDateTime(original?.updatedAt, DATE_YMD_HMS, { useLocalTime: false })}
                </div>
            )
        },
        {
            accessorKey: 'changeBy.userCode',
            header: t('field.update_by'),
            size: 300,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div>
                    {original?.changeBy?.userCode}
                </div>
            )
        }
    ];

    const onDetailHistory = (record) => {
        showModal({
            size: '2xl',
            title: t('words_title.history_change_detail'),
            titleClassName: 'text-center w-full',
            footerClassName: 'hidden',
            content: (
                <HistoryItem history={record} />
            )
        });
    };
    // #endregion History

    return (
        <div>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div className='layout-card'>
                        <h2 className='title-2'>{t('words_title.license_infomation')}</h2>
                        <div className='mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6'>
                            <FormField
                                control={form.control}
                                name='licenseType'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel
                                            required={!!watchRoleId && !isAdmin(Number(watchRoleId))}
                                        >
                                            {t('field.driver_license_type')}
                                        </FormLabel>
                                        <FormControl>
                                            <Combobox
                                                value={field.value?.toString() ?? ''}
                                                onChange={(item) => {
                                                    field.onChange(item?.code);
                                                    form.setValue('licenseType', item?.code);
                                                }}
                                                items={licenseTypeGas.map(item => ({
                                                    label: item.name,
                                                    value: item.id?.toString(),
                                                    code: item.id
                                                }))}
                                                search={false}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='licenseTypeOption'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className='hidden sm:block h-[20px]' />
                                        <FormControl>
                                            <div className='flex items-center min-h-[20px] sm:min-h-[48px]'>
                                                <RadioGroup
                                                    key={field.value}
                                                    onValueChange={(value) => {
                                                        field.onChange(value);
                                                        form.clearErrors('licenseBeforeImageId');
                                                        form.clearErrors('licenseAfterImageId');
                                                    }}
                                                    value={field.value?.toString()}
                                                    className='flex gap-6 w-full'
                                                >
                                                    {LICENSE_TYPE_OPTIONS.map((option, index) => (
                                                        <div
                                                            key={index}
                                                            className='flex items-center space-x-2'
                                                        >
                                                            <RadioGroupItem
                                                                value={option.value.toString()}
                                                                id={option.value.toString()}
                                                            />
                                                            <Label className='cursor-pointer font-normal' htmlFor={option.value.toString()}>
                                                                {option.name}
                                                            </Label>
                                                        </div>
                                                    ))}
                                                </RadioGroup>
                                            </div>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className='mt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4 md:gap-6'>
                            <div>
                                <ImageUploadAccount
                                    form={form}
                                    filedName='licenseBeforeImageId'
                                    caption={useMyNumberLicense ? t('field.myna_license') : t('words_title.before')}
                                    isUploadOCR={!useMyNumberLicense}
                                    urlImagePreview={beforeImgUrl}
                                    className='h-56'
                                    onUploadSuccess={uploadSuccessBefore}
                                    required={!!watchRoleId && !isAdmin(Number(watchRoleId))}
                                />
                            </div>
                            {!useMyNumberLicense && (
                                <div>
                                    <ImageUploadAccount
                                        filedName='licenseAfterImageId'
                                        form={form}
                                        caption={t('words_title.after')}
                                        urlImagePreview={afterImgUrl}
                                        className='h-56'
                                        required={!!watchRoleId && !isAdmin(Number(watchRoleId))}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className='layout-card mt-6'>
                        <h2 className='title-2'>{t('words_title.basic_info')}</h2>
                        {isEdit && (
                            <div className='mt-6 flex flex-col gap-2'>
                                <div className='col-span-full flex gap-1 items-center'>
                                    <IconUserCode />
                                    <Label>{`${t('field.user_code')}：`}</Label>
                                    <div className='leading-[1.25rem]'>{account?.userCode || '--'}</div>
                                </div>
                                <div className='col-span-full flex gap-1 items-center'>
                                    <IconAge width='1.4970703125em' height='1.1em' />
                                    <Label>ユーザー年齢：</Label>
                                    <div className='leading-[1.25rem]'>
                                        {getAgeText(account?.age)}
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className='mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6'>
                            <FormField
                                control={form.control}
                                name='roleId'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>{t('field.role')}</FormLabel>
                                        <FormControl>
                                            <Combobox
                                                value={field.value?.toString() ?? ''}
                                                onChange={(item) => {
                                                    field.onChange(item?.code);
                                                    form.setValue('roleId', item?.code);
                                                    form.trigger('licenseType');
                                                    form.trigger('licenseBeforeImageId');
                                                    form.trigger('licenseAfterImageId');
                                                }}
                                                items={roles.map(item => ({
                                                    label: t(`${item.name}`),
                                                    value: item.id?.toString(),
                                                    code: item.id
                                                }))}
                                                search={false}
                                                disableClearIcon
                                                disabled={disabledRole}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='fullname'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>{t('field.name_or_company_name')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                type='text'
                                                placeholder={t('placeholder.enter')}
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='furiganaName'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('field.furigana_name')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                type='text'
                                                placeholder={t('placeholder.enter')}
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='birthOfDate'
                                render={({ field, fieldState }) => (
                                    <FormItem>
                                        <FormLabel
                                            required={!!watchRoleId && !isAdmin(Number(watchRoleId))}
                                        >
                                            {t('field.date_of_birth')}
                                        </FormLabel>
                                        <FormControl>
                                            <Calendar
                                                mode='single'
                                                selected={field.value}
                                                onSelect={date => field.onChange(date)}
                                                useIconRemove={false}
                                                required={false}
                                                disabled={[
                                                    {
                                                        after: new Date()
                                                    }
                                                ]}
                                                isInvalid={!!fieldState.error}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='licenseExporeDate'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('field.license_expire_date')}</FormLabel>
                                        <FormControl>
                                            <Calendar
                                                mode='single'
                                                selected={field.value}
                                                onSelect={date => field.onChange(date)}
                                                useIconRemove={false}
                                                required={false}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='licenseNumber'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('field.driver_license_number')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                type='text'
                                                placeholder={t('placeholder.enter')}
                                                isHalfWidth
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='phone'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>{t('field.phone_number')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                type='text'
                                                placeholder={t('placeholder.enter')}
                                                maxLength={MAX_LENGTH_NUMBER}
                                                isHalfWidth
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='email'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>{t('field.email')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                type='text'
                                                placeholder={t('placeholder.enter')}
                                                isHalfWidth
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='zipCode'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('field.post_code')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                type='text'
                                                placeholder={t('placeholder.enter')}
                                                isHalfWidth
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='address'
                                render={({ field }) => (
                                    <FormItem className='col-span-full'>
                                        <FormLabel>{t('field.address')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                type='text'
                                                placeholder={t('placeholder.enter')}
                                                {...field}
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='status'
                                render={({ field }) => (
                                    <FormItem className='md:flex md:items-center col-span-full'>
                                        <FormLabel
                                            className='mb-2 md:mb-0 min-w-32 md:min-w-40'
                                            required
                                        >
                                            {t('field.status')}
                                        </FormLabel>
                                        <FormControl>
                                            <RadioGroup
                                                key={field.value}
                                                onValueChange={(value) => {
                                                    field.onChange(value);
                                                }}
                                                value={field.value?.toString()}
                                                className='flex gap-6 w-2/3'
                                            >
                                                <div className='flex items-center space-x-2'>
                                                    <RadioGroupItem
                                                        value={STATUS.Active.toString()}
                                                        id='active'
                                                    />
                                                    <Label className='cursor-pointer font-normal' htmlFor='active'>
                                                        {t('field.active')}
                                                    </Label>
                                                </div>
                                                <div className='flex items-center space-x-2'>
                                                    <RadioGroupItem
                                                        value={STATUS.Inactive.toString()}
                                                        id='inactive'
                                                    />
                                                    <Label className='cursor-pointer font-normal' htmlFor='inactive'>
                                                        {t('field.inactive')}
                                                    </Label>
                                                </div>
                                            </RadioGroup>
                                        </FormControl>
                                    </FormItem>
                                )}
                            />

                            {isEdit && (
                                <div className='col-span-full'>
                                    <PasswordAction />
                                </div>
                            )}

                            <FormField
                                control={form.control}
                                name='note'
                                render={({ field }) => (
                                    <FormItem className='col-span-full'>
                                        <FormLabel>{t('field.note')}</FormLabel>
                                        <FormControl>
                                            <Textarea
                                                placeholder={t('placeholder.enter')}
                                                className='min-h-28'
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                        </div>
                    </div>

                    {/* // History */}
                    {isEdit && (
                        <div className='layout-card mt-6'>
                            <h2 className='title-2 mb-6'>{t('words_title.history_change')}</h2>
                            <Table
                                columns={historyColumns}
                                data={historyChangeUser}
                                state={pagingHistoryChangeUser}
                                rowCount={totalRecordHistoryChangeUser}
                                onStateChange={onStateChange}
                                selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                                // getRowId={row => row?.id?.toString()}
                                showSelectPageSize={false}
                            />
                        </div>
                    )}

                    <div className='mt-6 flex justify-center gap-4'>
                        {isEdit ?
                            (
                                <>
                                    <ButtonCusWithIcon
                                        type='button'
                                        variant='outline'
                                        size='sm'
                                        iconId='#icon-delete'
                                        onClick={onDeleteAccount}
                                    >
                                        {t('button.delete')}
                                    </ButtonCusWithIcon>
                                    <ButtonCusWithIcon
                                        type='submit'
                                        variant='default'
                                        size='sm'
                                        iconId='#icon-update'
                                    >
                                        {t('button.update')}
                                    </ButtonCusWithIcon>
                                </>
                            ) :
                            (
                                <ButtonCus type='submit' variant='default' size='sm'>
                                    {t('button.add')}
                                </ButtonCus>
                            )}
                    </div>
                </form>
            </Form>
        </div>
    );
});
